import { useState, type FormEvent } from 'react';
import { Alert, Button, PasswordInput, Stack, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { changePasswordRequest, fetchMe } from './auth-api';
import { useAuthStore } from './auth-store';
import { ApiError } from '../lib/api-error';

interface Props {
  /** Called when change succeeded. Used by the force-change modal to unmount. */
  onSuccess?: () => void;
  submitLabel?: string;
}

const MIN_LENGTH = 12;

export function ChangePasswordForm({ onSuccess, submitLabel = 'Change password' }: Props) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function validate(): string | null {
    if (!current) return 'Enter your current password.';
    if (next.length < MIN_LENGTH) return `New password must be at least ${MIN_LENGTH} characters.`;
    if (next !== confirm) return 'New passwords do not match.';
    if (next === current) return 'New password must be different from the current one.';
    return null;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await changePasswordRequest(current, next);
      // Refresh user so must_change_password flips to false in the store.
      const freshUser = await fetchMe();
      useAuthStore.getState().setSession(freshUser, useAuthStore.getState().accessToken ?? '');
      notifications.show({
        title: 'Password changed',
        message: 'Your password is updated.',
        color: 'pine',
      });
      setCurrent('');
      setNext('');
      setConfirm('');
      onSuccess?.();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'wrong_current_password') setError('Your current password is incorrect.');
        else if (err.code === 'password_reuse')
          setError('New password must differ from the current one.');
        else if (err.code === 'validation_failed')
          setError(`New password must be at least ${MIN_LENGTH} characters.`);
        else setError(err.message || err.code);
      } else {
        setError('Network error. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit}>
      <Stack gap="sm">
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {error}
          </Alert>
        )}
        <PasswordInput
          label="Current password"
          value={current}
          onChange={(e) => setCurrent(e.currentTarget.value)}
          required
          autoComplete="current-password"
        />
        <PasswordInput
          label="New password"
          description={`At least ${MIN_LENGTH} characters. Cannot match the current one.`}
          value={next}
          onChange={(e) => setNext(e.currentTarget.value)}
          required
          autoComplete="new-password"
        />
        <PasswordInput
          label="Confirm new password"
          value={confirm}
          onChange={(e) => setConfirm(e.currentTarget.value)}
          required
          autoComplete="new-password"
        />
        <Button type="submit" loading={submitting}>
          {submitLabel}
        </Button>
        <Text size="xs" c="dimmed">
          Changing your password signs out every other active session.
        </Text>
      </Stack>
    </form>
  );
}
